import { XbotHistory } from "@dto/pyxbot-agent.dto";
import { Label } from "src/data/Label";

// Key is versioned: an older saved conversation has no agent thread/answers and can't be drawn or continued.
const HISTORY_KEY = "xm-pyxbot-threads-agent";
const HISTORY_CAP = 50;

/**
 * The history is the signed-in person's own: kept under their user id, so whoever signs in next in the same tab
 * never sees it (the conversations hold answers with data the next person may not be allowed to see). No key while
 * nobody is known: a key shared by "nobody" would hand one person's answers to the next.
 */
export function historyKey(userId: number | null | undefined): string | null {
  return typeof userId === "number" && Number.isFinite(userId) ? `${HISTORY_KEY}:${userId}` : null;
}

/**
 * Who the history belongs to: the signed-in person's id, or undefined while that is not known yet. Every entry
 * point takes it, so this module imports none of the app's globals and a test hands it an id outright
 * (historyOwner.ts reads XorMon's signed-in user for the components).
 */
export type HistoryOwner = number | null | undefined;

/** The history kept before it was the person's own, under one key for everyone: never read again, so dropped. */
function forgetSharedHistory(): void {
  try {
    sessionStorage.removeItem(HISTORY_KEY);
  } catch {
    // a browser that keeps nothing has nothing to forget
  }
}

export function loadPyxbotHistory(owner: HistoryOwner): XbotHistory[] {
  forgetSharedHistory();
  const key = historyKey(owner);
  if (!key) {
    return [];
  }
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw) as XbotHistory[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function savePyxbotHistory(history: XbotHistory[], owner: HistoryOwner): void {
  const key = historyKey(owner);
  if (!key) {
    return;
  }
  try {
    sessionStorage.setItem(key, JSON.stringify(history.slice(0, HISTORY_CAP)));
  } catch {
    // loaded when needed: the app's logger brings the app's globals, which this module keeps out of its tests
    void import("src/service/log.service").then(({ Log }) => Log.warn(`Could not persist ${Label.XORBOT} history`));
  }
}

/**
 * The conversation first, then the others. An empty list in hand is the one case worth re-reading the browser for:
 * a list read before the person was known came back empty, and writing it would wipe theirs. Once it holds
 * anything it is already the merged list, so reading and parsing the whole history again - twice a turn, since
 * both the question and the answer are kept - would only cost time (and a delete writes the browser too, so
 * nothing deleted comes back).
 */
export function upsertPyxbotHistory(history: XbotHistory[], conversation: XbotHistory, owner: HistoryOwner): XbotHistory[] {
  const seen = new Set<string>();
  const kept = history.length ? history : loadPyxbotHistory(owner);
  const next = [conversation, ...kept].filter((one) => !seen.has(one.localId) && Boolean(seen.add(one.localId)));
  savePyxbotHistory(next, owner);
  return next;
}

export function removePyxbotHistory(history: XbotHistory[], localId: string, owner: HistoryOwner): XbotHistory[] {
  const next = history.filter((h) => h.localId !== localId);
  savePyxbotHistory(next, owner);
  return next;
}
